import { motion, useScroll, useTransform } from "framer-motion";
import { Send, LineChart } from "lucide-react";
import Countdown from "./Countdown";
import ContractCard from "./ContractCard";
import HoldersCard from "./HoldersCard";
import { DEX_URL, TG_URL } from "../config";

const BULB_COLORS = ["#FF2B4A", "#00F5D4", "#FFD700", "#00E676"];

const reveal = (i) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { duration: 0.9, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
});

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.7 },
});

export default function Hero() {
  const { scrollY } = useScroll();
  const glowY = useTransform(scrollY, [0, 700], [0, 140]);

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden pb-20 pt-48 sm:pt-56">
      <motion.div style={{ y: glowY }} className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-[#FF2B4A]/15 blur-[140px]" />
        <div className="absolute -left-40 top-40 h-[420px] w-[420px] rounded-full bg-[#00F5D4]/10 blur-[120px]" />
        <div className="absolute -right-40 top-64 h-[420px] w-[420px] rounded-full bg-[#9945FF]/10 blur-[120px]" />
      </motion.div>

      <div className="relative mx-auto max-w-6xl px-5">
        <div className="mb-12 flex justify-center gap-4 sm:gap-7" aria-hidden="true">
          {Array.from({ length: 16 }).map((_, i) => (
            <span
              key={i}
              className="light-bulb"
              style={{
                backgroundColor: BULB_COLORS[i % 4],
                boxShadow: `0 0 14px 3px ${BULB_COLORS[i % 4]}66`,
                animationDelay: `${(i % 4) * 0.45}s`,
              }}
            />
          ))}
        </div>

        <motion.p
          {...fadeUp(0.1)}
          data-testid="hero-eyebrow"
          className="mb-6 text-center font-mono-x text-[10px] uppercase tracking-[0.35em] text-[#00F5D4] sm:text-xs"
        >
          MerrySolmas.com · Community Takeover · Solana
        </motion.p>

        <h1 className="text-center font-display font-black leading-[0.9] tracking-tight">
          <span className="block overflow-hidden pb-1 text-[17vw] sm:text-8xl lg:text-9xl">
            <motion.span
              {...reveal(0)}
              className="block bg-gradient-to-r from-[#FF2B4A] via-[#FFD700] to-[#00F5D4] bg-clip-text text-transparent"
            >
              SOLMAS
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-3 text-[10vw] text-slate-50 sm:text-6xl lg:text-7xl">
            <motion.span {...reveal(1)} className="block">
              IS COMING!
            </motion.span>
          </span>
        </h1>

        <motion.p
          {...fadeUp(0.7)}
          data-testid="hero-subtitle"
          className="mx-auto mt-6 max-w-2xl text-center text-base text-slate-400 sm:text-lg"
        >
          We CTO'd the most festive token on Solana. No dev, no cabal — just a community of degens riding the sleigh
          to December 25, 2026.
        </motion.p>

        <motion.div {...fadeUp(0.85)} className="mt-12">
          <p className="mb-4 text-center font-mono-x text-[11px] uppercase tracking-[0.3em] text-[#FF2B4A]">
            Countdown to Solmas 2026
          </p>
          <Countdown />
        </motion.div>

        <motion.div {...fadeUp(1)} className="mx-auto mt-10 max-w-2xl">
          <ContractCard />
        </motion.div>

        <motion.div {...fadeUp(1.1)} className="mt-8 flex flex-col items-center justify-center gap-6 lg:flex-row">
          <HoldersCard />
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              data-testid="hero-buy-button"
              href={DEX_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#00F5D4] to-[#00E676] px-7 py-3.5 font-display text-sm font-extrabold text-black shadow-[0_0_20px_rgba(0,245,212,0.4)] transition-all duration-200 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(0,245,212,0.7)] active:scale-[0.97]"
            >
              <LineChart className="h-4 w-4" />
              Buy $SOLMAS
            </a>
            <a
              data-testid="hero-telegram-button"
              href={TG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-[#FF2B4A]/50 bg-[#FF2B4A]/10 px-7 py-3.5 font-display text-sm font-extrabold text-[#FF2B4A] transition-all duration-200 hover:scale-[1.03] hover:bg-[#FF2B4A]/20 hover:shadow-[0_0_30px_rgba(255,43,74,0.4)] active:scale-[0.97]"
            >
              <Send className="h-4 w-4" />
              Join Telegram
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
